import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnlineStatus } from './useOnlineStatus';
import {
  getSyncState,
  refreshPendingCount,
  runOfflineSync,
  subscribeSyncState,
  type SyncState,
} from '@/lib/offline-sync-engine';

export type { SyncState } from '@/lib/offline-sync-engine';

const RETRY_PENDING_MS = 30_000;

/**
 * Starts the offline sync (POS-I64). Mount it ONCE, in the app shell: it runs the engine on every
 * reconnect and re-tries sales that are still pending (network errors / 5xx) every 30 s while the
 * till is online. Everything else reads the shared state through `useOfflineSync`.
 */
export function useOfflineSyncRunner() {
  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();

  useEffect(() => {
    void refreshPendingCount();
    const interval = setInterval(() => void refreshPendingCount(), 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isOnline) return;
    // Small delay so the connection stabilises before attempting.
    const first = setTimeout(() => void runOfflineSync({ queryClient }), 2000);
    const again = setInterval(() => void runOfflineSync({ queryClient }), RETRY_PENDING_MS);
    return () => {
      clearTimeout(first);
      clearInterval(again);
    };
  }, [isOnline, queryClient]);
}

/** Read-only view of the shared offline sync state, plus a manual "retry failed". */
export function useOfflineSync(): SyncState & { retryFailed: () => void } {
  const state = useSyncExternalStore(subscribeSyncState, getSyncState, getSyncState);
  const queryClient = useQueryClient();
  const retryFailed = useCallback(() => {
    void runOfflineSync({ includeFailed: true, queryClient });
  }, [queryClient]);
  return { ...state, retryFailed };
}
